import React, { useCallback, useState } from 'react'

import { Box, Grid, Stack } from '@mui/material'

import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { FacebookPostComment, FacebookPost } from '@3m5/sm_publish-frontend-api'
import ProfileImage from '../ProfileImage'
import LinkHeadline from '../LinkHeadline'
import SubHeadline from '../SubHeadline'
import formatDate from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import getDateFormat from '../../../../../../defaults/getDateFormat'
import Text from '../Text'
import CommentLike from './modules/CommentLike'
import CommentDelete from './modules/CommentDelete'
import { useDispatch, useSelector } from 'react-redux'
import { DeleteFacebookPostCommentRequest } from '@3m5/sm_publish-frontend-api'
import { deleteFacebookPostCommentInitiated } from '../../../../../../store/facebook/facebook.actions'
import DialogComponent from '@professocial/professocialFormComponents/FormComponents/DialogComponent'
import { useTranslation } from 'react-i18next'
import { PostingRootState } from '../../../../../../store/postingRoot.reducer'
import { namedRequestData } from '../../../../../../store/common/common.selectors'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'
import { NetworkType } from '@3m5/sm_publish-frontend-api'

interface ISingleCommentProps {
  comment: FacebookPostComment
  post: FacebookPost
  networkPageId: string
  networkType?: NetworkType
  onChange?: (value: any) => void
}

const SingleFacebookComment: React.FC<ISingleCommentProps> = (props) => {
  const comment = props.comment
  const post = props.post
  const [isShown, setIsShown] = useState(false)
  const [showDeleteCommentWarning, setShowDeleteCommentWarning] = useState<boolean>(false)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const dispatchDeletePostComment = useCallback((params: DeleteFacebookPostCommentRequest) => dispatch(deleteFacebookPostCommentInitiated(params)), [dispatch])
  const actionOnDeleteComment: ActionCall | undefined = useSelector((state: PostingRootState) => namedRequestData(state, deleteFacebookPostCommentInitiated.type))

  const deletePost = () => {
    if (comment.networkCommentId) {
      const params: DeleteFacebookPostCommentRequest = {
        networkPageId: props.networkPageId,
        networkPostId: props.post.networkPostId,
        networkCommentId: comment.networkCommentId,
      }
      comment.networkCommentId.indexOf('#') === -1 ? dispatchDeletePostComment(params) // only comments without # in id
        : props.onChange && props.onChange(params)
    }
  }
  const processing = actionOnDeleteComment?.inProgress && actionOnDeleteComment?.id === comment.networkCommentId
  return (
    <>
      <Grid
        container
        item
        xs={12}
        sx={{ pt: 3 }}
        onMouseEnter={() => setIsShown(true)}
        onMouseLeave={() => setIsShown(false)}
      >
        <Grid container spacing={1}>
          <Grid item xs='auto'>
            <ProfileImage
              data={`https://graph.facebook.com/${comment.postedByNetworkId}/picture`}
            />
          </Grid>
          <Grid xs item>
            <Stack direction='row' alignItems='center'>
              <Box>
                <Stack direction='column'>
                  <LinkHeadline
                    networkPageId={props.networkPageId}
                    post={comment}
                    networkType={props.networkType}
                    value={{
                      url: `https://www.facebook.com/${post.postedByNetworkName}`,
                      label: post.postedByNetworkName,
                    }}
                  />
                  <SubHeadline
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={comment.createdAt && formatDate(new Date(comment.createdAt).getTime(), getDateFormat('dateTime'))}
                  />
                </Stack>
                <Grid item xs={12} sx={{ mt: 1 }}>
                  <Text
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    value={comment.message}
                  />
                </Grid>
                <Stack direction='row' spacing={2}>
                  <Box>
                    <CommentLike
                      networkPageId={props.networkPageId}
                      post={post}
                      networkType={props.networkType}
                      value={comment}
                    />
                  </Box>
                </Stack>
              </Box>
              <Box marginLeft='auto' width='50px'>
                {isShown && !processing && (
                  <CommentDelete
                    networkPageId={props.networkPageId}
                    post={post}
                    networkType={props.networkType}
                    onClickDelete={() => setShowDeleteCommentWarning(true)}
                  />
                )}
                {processing && <Preloader width='auto' minHeight='0' size={24} />}
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
      <DialogComponent
        type='error'
        title={t('networkContentMessages:deleteWarningTitle')}
        message={t('networkContentMessages:deleteCommentWarning')}
        open={showDeleteCommentWarning}
        confirmLabel={t('networkContentCommon:delete')}
        cancelLabel={t('networkContentCommon:cancel')}
        autoClose={false}
        requestActions
        onClose={() => setShowDeleteCommentWarning(false)}
        onCancel={() => setShowDeleteCommentWarning(false)}
        onConfirm={() => {
          setShowDeleteCommentWarning(false)
          deletePost()
        }}
      />
    </>
  )
}

export default SingleFacebookComment
